import { useTranslation } from 'react-i18next'
import { Outlet, useMatch, useNavigate, useParams } from 'react-router'
import { useCallback, useMemo } from 'react'
import { EcommerceCommonHeader } from '@/components/shared/headers'
import { PageBody, PageHeader } from '@/components/shared/page-sections'
import { ScrollableRouteContainer } from '@/components/shared/Container'
import { useFinancialModelSettingsStore } from '@/feature/financial-model/settings/store/financialModelSettingsStore'
import { ModuleNavigation, TabValue } from '@/components/shared/ModuleNavigation'
import { Show } from '@/components/shared/show'
import { useOwcStore } from '@/feature/financial-model/ecommerce/owc/store'
import { ExportModuleDialog } from '@/feature/export/ecommerce/components/dowload'
import { ViewSwitcher } from '@/components/shared/views-buttons'
import { useOwcSetupStatus } from '@/feature/financial-model/ecommerce/owc/hooks/useOwcSetupStatus'

const OwcRoute = () => {
  const { id: financialModelId } = useParams<{ id: string }>()
  const financialModelName = useFinancialModelSettingsStore(state => state.settings[financialModelId!].general?.name)
  const navigate = useNavigate()
  const handleTabsChange = useCallback(
    (value: TabValue) => {
      const tabNavigationMap: Record<TabValue, string> = {
        'set-up': `/dashboard/financial-model/${financialModelId}/ecommerce/other-working-capital`,
        'historical-data': `/dashboard/financial-model/${financialModelId}/ecommerce/other-working-capital/historical-data`,
        'forecast-data': `/dashboard/financial-model/${financialModelId}/ecommerce/other-working-capital/forecast-data`,
        total: `/dashboard/financial-model/${financialModelId}/ecommerce/other-working-capital/total`
      }
      navigate(tabNavigationMap[value])
    },
    [financialModelId, navigate]
  )

  const isOwcTotalsRoute = useMatch({
    path: `/dashboard/financial-model/${financialModelId}/ecommerce/other-working-capital/total`
  })

  const { isCurrentAssetsSetupCompleted, isCurrentLiabilitiesSetupCompleted, isTaxesSetupCompleted, isLoading } = useOwcSetupStatus(financialModelId!)

  // Placeholder for setup completion logic
  const isSetupCompleted = useMemo(() => {
    if (isLoading) return false

    return isCurrentAssetsSetupCompleted || isCurrentLiabilitiesSetupCompleted || isTaxesSetupCompleted
  }, [isCurrentAssetsSetupCompleted, isCurrentLiabilitiesSetupCompleted, isLoading, isTaxesSetupCompleted])

  return (
    <ScrollableRouteContainer className={`p-0`}>
      <Show when={!!isOwcTotalsRoute} fallback={<SetupPageHeader financialModelName={financialModelName!} financialModelId={financialModelId!} />}>
        <TotalsPageHeader financialModelName={financialModelName!} financialModelId={financialModelId!} />
      </Show>

      <ModuleNavigation isSetupCompleted={isSetupCompleted} handleTabsChange={handleTabsChange} isTotalAllowed={isSetupCompleted}>
        <PageBody>
          <Outlet />
        </PageBody>
      </ModuleNavigation>
    </ScrollableRouteContainer>
  )
}

const SetupPageHeader = (props: { financialModelName: string; financialModelId: string }) => {
  const { t } = useTranslation(`owc`)
  const { financialModelName, financialModelId } = props
  return (
    <PageHeader className={`pb-0`}>
      <EcommerceCommonHeader linkText={t(`common.header.back`)} path={`/dashboard/financial-model/${financialModelId}/ecommerce`} />
      <div className='mt-6'>
        <h1 className='page-title'>{t(`common.header.title`)}</h1>
        <p className='page-subtitle'>{financialModelName}</p>
      </div>
    </PageHeader>
  )
}

const TotalsPageHeader = (props: { financialModelName: string; financialModelId: string }) => {
  const { t } = useTranslation(`owc`)
  const { financialModelName, financialModelId } = props

  const view = useOwcStore(state => state.view)
  const setView = useOwcStore(state => state.setView)

  const exportOptions = useMemo(
    () => ({
      module: `owc` as const,
      view
    }),
    [view]
  )

  return (
    <PageHeader className={`pb-0`}>
      <EcommerceCommonHeader linkText={t(`common.header.back`)} path={`/dashboard/financial-model/${financialModelId}/ecommerce`} />
      <div className='mt-6 flex items-center justify-between'>
        <div>
          <h1 className='page-title'>{t(`totals.title`)}</h1>
          <p className='page-subtitle'>{financialModelName}</p>
        </div>
        <div className='flex items-center gap-2'>
          <ExportModuleDialog financialModelId={financialModelId!} options={exportOptions} />
          <ViewSwitcher view={view} onViewChange={setView} />
        </div>
      </div>
    </PageHeader>
  )
}

export default OwcRoute
